import { observer } from "mobx-react";
import { Battery, BatteryCharging, BatteryFull, BatteryLow, BatteryMedium } from "lucide-react";
import type { Agv, AgvStatus } from "../types";
import { store } from "../../store";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { Badge, type BadgeProps } from "@/components/ui/badge";

const statusConfig: Record<AgvStatus, { label: string; variant: BadgeProps["variant"] }> = {
    moving: { label: "Moving", variant: "lightBlue" },
    waiting: { label: "Waiting", variant: "lightOrange" },
    charging: { label: "Charging", variant: "lightGreen" },
    idle: { label: "Idle", variant: "secondary" },
    returning: { label: "Returning", variant: "lightPurple" },
    errored: { label: "Error", variant: "lightRed" },
};

export type AgvCardProps = {
    agv: Agv;
};

export const AgvCard = observer(({ agv }: AgvCardProps): JSX.Element => {
    const { selectedAgvId, missions } = store.serviceAgv;
    const isSelected = selectedAgvId === agv.id;

    const mission = agv.currentMissionId
        ? missions.find((m) => m.id === agv.currentMissionId)
        : null;

    const status = statusConfig[agv.status];

    return (
        <button
            onClick={() => store.serviceAgv.setSelectedAgv(agv.id)}
            className={cn(
                `border-border w-full cursor-pointer rounded-lg border p-3 text-left transition-all`,
                isSelected ? "bg-muted" : "bg-white hover:shadow-sm",
            )}
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <div>
                        <div className="text-sm font-semibold">{agv.name}</div>
                        <div className="text-muted-foreground text-xs">
                            {agv.manufacturer} {agv.model}
                        </div>
                    </div>
                </div>
                <Badge variant={status.variant}>{status.label}</Badge>
            </div>

            <div className="mt-2 flex items-center justify-between gap-3">
                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    {agv.status !== "charging" && agv.battery < 20 && <Battery />}
                    {agv.status !== "charging" && agv.battery < 40 && agv.battery >= 20 && (
                        <BatteryLow />
                    )}
                    {agv.status !== "charging" && agv.battery < 80 && agv.battery >= 40 && (
                        <BatteryMedium />
                    )}
                    {agv.status !== "charging" && agv.battery >= 80 && <BatteryFull />}
                    {agv.status === "charging" && <BatteryCharging />}

                    <span className={agv.battery < 20 ? "font-medium text-red-600" : ""}>
                        {agv.battery}%
                    </span>
                </div>
                {mission && (
                    <div className="text-muted-foreground truncate text-xs">{mission.name}</div>
                )}
            </div>
            {agv.status === "moving" && (
                <Progress value={agv.pathProgress * 100} max={100} className="mt-2" />
            )}
        </button>
    );
});
